import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Users, Plus, Edit, Trash2, X, Save } from 'lucide-react'
import { apiFetch } from '../../config/api'
import PageHeader from '../../components/Dashboard/PageHeader'

interface User {
  id: number
  email: string
  last_login: string | null
  created_at: string
}

export default function UsersManagement() {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [formData, setFormData] = useState({ email: '', password: '' })

  const getAuthToken = () => {
    return localStorage.getItem('dashboard_token') || ''
  }

  const fetchUsers = async () => {
    try {
      setLoading(true)
      const response = await apiFetch('/users', {
        headers: {
          'Authorization': getAuthToken(),
        },
      })

      const data = await response.json()

      if (response.ok && data.success) {
        setUsers(data.users)
        setError('')
      } else {
        setError(data.error || 'Fehler beim Laden der Benutzer')
      }
    } catch (err) {
      setError('Verbindungsfehler')
      console.error('Fetch users error:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [])

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!formData.email || !formData.password) {
      setError('Bitte füllen Sie alle Felder aus.')
      return
    }

    try {
      const response = await apiFetch('/users', {
        method: 'POST',
        headers: {
          'Authorization': getAuthToken(),
        },
        body: JSON.stringify(formData),
      })

      const data = await response.json()

      if (response.ok && data.success) {
        setShowAddForm(false)
        setFormData({ email: '', password: '' })
        fetchUsers()
      } else {
        setError(data.error || 'Fehler beim Erstellen des Benutzers')
      }
    } catch (err) {
      setError('Verbindungsfehler')
      console.error('Add user error:', err)
    }
  }

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!formData.email) {
      setError('E-Mail ist erforderlich.')
      return
    }

    if (!editingUser) return

    try {
      const response = await apiFetch('/users', {
        method: 'PUT',
        headers: {
          'Authorization': getAuthToken(),
        },
        body: JSON.stringify({
          id: editingUser.id,
          email: formData.email,
          password: formData.password || undefined,
        }),
      })

      const data = await response.json()

      if (response.ok && data.success) {
        setEditingUser(null)
        setFormData({ email: '', password: '' })
        fetchUsers()
      } else {
        setError(data.error || 'Fehler beim Aktualisieren des Benutzers')
      }
    } catch (err) {
      setError('Verbindungsfehler')
      console.error('Update user error:', err)
    }
  }

  const handleDeleteUser = async (userId: number) => {
    if (!confirm('Möchten Sie diesen Benutzer wirklich löschen?')) {
      return
    }

    try {
      const response = await apiFetch(`/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': getAuthToken(),
        },
      })

      const data = await response.json()

      if (response.ok && data.success) {
        fetchUsers()
      } else {
        setError(data.error || 'Fehler beim Löschen des Benutzers')
      }
    } catch (err) {
      setError('Verbindungsfehler')
      console.error('Delete user error:', err)
    }
  }

  const startEdit = (user: User) => {
    setEditingUser(user)
    setFormData({ email: user.email, password: '' })
    setShowAddForm(false)
  }

  const cancelEdit = () => {
    setEditingUser(null)
    setFormData({ email: '', password: '' })
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Nie'
    const date = new Date(dateString)
    return date.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <div className="w-full min-w-0 max-w-full">
      <PageHeader
        title="Benutzer"
        description="Zugänge für das Dashboard verwalten."
        actions={
          !showAddForm && !editingUser ? (
            <button
              onClick={() => {
                setShowAddForm(true)
                setEditingUser(null)
                setFormData({ email: '', password: '' })
              }}
              className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-brand-800 text-white text-sm font-medium hover:bg-brand-700"
            >
              <Plus className="h-4 w-4" />
              Neuer Benutzer
            </button>
          ) : undefined
        }
      />

      {error && (
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-4 bg-red-500/10 border border-red-500/30 text-red-400 rounded-xl"
        >
          {error}
        </motion.div>
      )}

      {/* Add/Edit Form */}
      {(showAddForm || editingUser) && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 relative bg-gradient-to-br from-gray-900/80 to-gray-900/80 backdrop-blur-xl rounded-xl shadow-lg border border-white/10 p-6 overflow-hidden"
        >
          {/* Subtle gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] to-transparent pointer-events-none" />
          
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold text-white mb-2">
                  {editingUser ? 'Benutzer bearbeiten' : 'Neuen Benutzer erstellen'}
                </h2>
                <div className="h-px w-16 bg-gradient-to-r from-brand-500 to-brand-500" />
              </div>
              <motion.button
                onClick={() => {
                  setShowAddForm(false)
                  cancelEdit()
                }}
                className="text-gray-400 hover:text-white transition-colors p-2 rounded-lg hover:bg-white/5"
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
              >
                <X className="h-6 w-6" />
              </motion.button>
            </div>

            <form onSubmit={editingUser ? handleUpdateUser : handleAddUser} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  E-Mail
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-brand-500/50 focus:border-brand-500/50 transition-all duration-300 outline-none"
                  placeholder="benutzer@email.de"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Passwort {editingUser && <span className="text-gray-400">(leer lassen, um nicht zu ändern)</span>}
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-brand-500/50 focus:border-brand-500/50 transition-all duration-300 outline-none"
                  placeholder="••••••••"
                  required={!editingUser}
                />
              </div>

              <div className="flex gap-3 pt-2">
                <motion.button
                  type="submit"
                  className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-brand-600 to-brand-600 border border-brand-500/30 text-white rounded-xl hover:from-brand-500 hover:to-brand-500 transition-all duration-300 shadow-lg shadow-brand-600/20"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Save className="h-5 w-5" />
                  {editingUser ? 'Aktualisieren' : 'Erstellen'}
                </motion.button>
                <motion.button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false)
                    cancelEdit()
                  }}
                  className="px-6 py-3 bg-white/5 border border-white/10 text-gray-300 rounded-xl hover:bg-white/10 hover:border-white/20 transition-all duration-300"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Abbrechen
                </motion.button>
              </div>
            </form>
          </div>
        </motion.div>
      )}

      {/* Users Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative bg-gradient-to-br from-gray-900/80 to-gray-900/80 backdrop-blur-xl rounded-xl shadow-lg border border-white/10 overflow-hidden"
      >
        {/* Subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] to-transparent pointer-events-none" />
        
        <div className="relative z-10">
          {loading ? (
            <div className="p-12 text-center text-gray-400">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="inline-block"
              >
                <Users className="h-8 w-8" />
              </motion.div>
              <p className="mt-4">Lade Benutzer...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-300">Keine Benutzer gefunden</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-white/10">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      E-Mail
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Erstellt am
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Letzter Login
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Aktionen
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {users.map((user, index) => (
                    <motion.tr 
                      key={user.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="hover:bg-white/5 transition-colors group"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                        {user.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white font-medium">
                        {user.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                        {formatDate(user.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                        {formatDate(user.last_login)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-3">
                          <motion.button
                            onClick={() => startEdit(user)}
                            className="text-brand-400 hover:text-brand-300 transition-colors"
                            title="Bearbeiten"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                          >
                            <Edit className="h-5 w-5" />
                          </motion.button>
                          <motion.button
                            onClick={() => handleDeleteUser(user.id)}
                            className="text-red-400 hover:text-red-300 transition-colors"
                            title="Löschen"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                          >
                            <Trash2 className="h-5 w-5" />
                          </motion.button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  )
}
